import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { joinClass, leaveClass, listJoinedClasses } from '@/api/classes';
import { ClassDetailDialog } from '@/components/class/class-detail-dialog';
import { Button } from '@/components/ui/button/button';
import type { ClassroomOut } from '@/types';

import { JoinClassDialog } from './JoinClassDialog';
import styles from './my-classes.module.css';

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
}

export function MyClassesPage() {
  const navigate = useNavigate();

  const { data: classes, isLoading, refetch } = useQuery({
    queryKey: ['joined-classes'],
    queryFn: () => listJoinedClasses(),
  });

  // toast
  const [toast, setToast] = useState('');
  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(''), 2200);
  };

  // 加入弹窗
  const [joinOpen, setJoinOpen] = useState(false);

  const handleJoined = async () => {
    setJoinOpen(false);
    showToast('加入成功');
    await refetch();
  };

  // 退出确认弹窗
  const [leaveTarget, setLeaveTarget] = useState<ClassroomOut | null>(null);
  const [leaving, setLeaving] = useState(false);

  const handleLeave = async () => {
    if (!leaveTarget) return;
    setLeaving(true);
    try {
      await leaveClass(leaveTarget.id);
      setLeaveTarget(null);
      showToast('已退出班级');
      await refetch();
    } catch (err) {
      showToast(err instanceof Error ? err.message : '退出失败');
    } finally {
      setLeaving(false);
    }
  };

  // 班级详情弹窗（公告 / 排行榜 / 资料区）
  const [previewTarget, setPreviewTarget] = useState<ClassroomOut | null>(null);

  const list = classes ?? [];
  const isEmpty = !isLoading && list.length === 0;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>我的班级</h1>
          <p className={styles.subtitle}>
            在这里查看已加入的班级与老师布置的作业。
          </p>
        </div>
        {!isEmpty && (
          <Button onClick={() => setJoinOpen(true)} className={styles.createBtn}>
            加入新班级
          </Button>
        )}
      </header>

      {/* 加载状态 */}
      {isLoading && (
        <div className={styles.loadingBox}>
          <div className={styles.spinner} />
          <span className={styles.loadingText}>正在加载班级...</span>
        </div>
      )}

      {/* 空状态 */}
      {isEmpty && (
        <div className={styles.emptyBox}>
          <div className={styles.emptyIcon}>
            <svg width="96" height="96" viewBox="0 0 96 96" fill="none">
              <rect x="20" y="16" width="56" height="64" rx="8" fill="#E8F1FF" />
              <rect x="30" y="28" width="36" height="6" rx="3" fill="#B3D4FF" />
              <rect x="30" y="42" width="36" height="4" rx="2" fill="#D6E8FF" />
              <rect x="30" y="52" width="24" height="4" rx="2" fill="#D6E8FF" />
              <circle cx="70" cy="64" r="16" fill="#007AFF" />
              <path
                d="M64 64l4 4 8-10"
                stroke="#fff"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <p className={styles.emptyTitle}>你还没有加入任何班级</p>
          <p className={styles.emptyDesc}>
            输入老师提供的邀请码或链接，即可加入班级查看作业。
          </p>
          <Button onClick={() => setJoinOpen(true)} className={styles.emptyBtn}>
            用邀请码 / 链接加入班级
          </Button>
        </div>
      )}

      {/* 班级卡片网格 */}
      {!isLoading && list.length > 0 && (
        <div className={styles.grid}>
          {list.map((c) => {
            const archived = c.status === 'archived';
            return (
              <div
                key={c.id}
                className={styles.classCard}
                onClick={() => setPreviewTarget(c)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') setPreviewTarget(c);
                }}
                role="button"
                tabIndex={0}
              >
                <div className={styles.cardTop}>
                  <div className={styles.cardName}>{c.name}</div>
                  {archived && <span className={styles.archivedTag}>已归档</span>}
                </div>
                <div className={styles.cardTeacher}>
                  教师团队：
                  {(c.teachers ?? []).length > 0
                    ? (c.teachers ?? [])
                        .map((t) => t.display_name + (t.is_owner ? '（创建者）' : ''))
                        .join('、')
                    : c.teacher_name || '—'}
                </div>
                {c.description ? (
                  <p className={styles.cardDesc}>{c.description}</p>
                ) : (
                  <p className={styles.cardDesc + ' ' + styles.muted}>暂无班级简介</p>
                )}
                <div className={styles.cardMeta}>
                  <span className={styles.metaItem}>
                    <span className={styles.metaIcon}>👥</span>
                    {c.member_count} 人
                  </span>
                  <span className={styles.metaItem}>
                    <span className={styles.metaIcon}>📋</span>
                    {c.assignment_count} 项作业
                  </span>
                  <span className={styles.metaItem}>
                    <span className={styles.metaIcon}>📢</span>
                    {c.announcement_count ?? 0} 公告
                  </span>
                  <span className={styles.metaItem}>
                    <span className={styles.metaIcon}>📎</span>
                    {c.material_count ?? 0} 资料
                  </span>
                </div>
                <div className={styles.cardHint}>点击卡片查看公告 / 排行榜 / 资料</div>
                {c.latest_job && (
                  <div className={styles.latestJob}>
                    <span className={styles.latestLabel}>最新作业</span>
                    <span className={styles.latestTitle} title={c.latest_job.title}>
                      {c.latest_job.title}
                    </span>
                    {c.latest_job.due_date && (
                      <span className={styles.latestDue}>
                        截止 {formatDate(c.latest_job.due_date)}
                      </span>
                    )}
                  </div>
                )}
                <div className={styles.cardActions} onClick={(e) => e.stopPropagation()}>
                  <Button
                    variant="secondary"
                    onClick={() => navigate(`/jobs?classId=${c.id}`)}
                  >
                    进入班级
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => setLeaveTarget(c)}
                    disabled={archived}
                    title={archived ? '已归档班级无需退出' : ''}
                  >
                    退出班级
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* 加入班级弹窗 */}
      <JoinClassDialog
        open={joinOpen}
        onClose={() => setJoinOpen(false)}
        onJoined={handleJoined}
        joinClass={joinClass}
      />

      {/* 退出班级二次确认 */}
      {leaveTarget && (
        <div className={styles.overlay} onClick={() => !leaving && setLeaveTarget(null)}>
          <div className={styles.confirmDialog} onClick={(e) => e.stopPropagation()}>
            <h3 className={styles.confirmTitle}>退出班级</h3>
            <p className={styles.confirmText}>
              确定要退出「<b>{leaveTarget.name}</b>」吗？退出后将无法查看该班级布置的作业。
            </p>
            <div className={styles.confirmActions}>
              <Button variant="secondary" onClick={() => setLeaveTarget(null)} disabled={leaving}>
                取消
              </Button>
              <Button variant="danger" onClick={handleLeave} loading={leaving}>
                确认退出
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 班级详情弹窗：公告 / 排行榜 / 资料区 */}
      {previewTarget && (
        <ClassDetailDialog
          classroom={previewTarget}
          isTeacher={false}
          onClose={() => setPreviewTarget(null)}
        />
      )}

      {/* toast */}
      {toast && <div className={styles.toast}>{toast}</div>}
    </div>
  );
}
